// Authentication Client for Admin Panel
// Handles login, logout, token refresh, and authenticated API requests

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api';

// Token storage keys
const ACCESS_TOKEN_KEY = 'amped_access_token';
const REFRESH_TOKEN_KEY = 'amped_refresh_token';
const USER_KEY = 'amped_user';

// Token management
export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getStoredUser() {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
}

function setTokens(accessToken, refreshToken) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function setUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

// Check if user is authenticated
export function isAuthenticated() {
  return !!getAccessToken();
}

// Login
export async function login(email, password) {
  try {
    const response = await fetch(`${API_ENDPOINT}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    // Store tokens and user
    setTokens(data.tokens.accessToken, data.tokens.refreshToken);
    setUser(data.user);

    return {
      success: true,
      user: data.user,
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

// Logout
export async function logout(logoutAll = false) {
  try {
    const refreshToken = getRefreshToken();
    const accessToken = getAccessToken();

    await fetch(`${API_ENDPOINT}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({ refreshToken, logoutAll }),
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearAuth();
  }
}

// Refresh access token
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(`${API_ENDPOINT}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Refresh token invalid/expired - clear auth
      clearAuth();
      throw new Error(data.message || 'Token refresh failed');
    }

    // Store new tokens
    setTokens(data.tokens.accessToken, data.tokens.refreshToken);
    setUser(data.user);

    return data.tokens.accessToken;
  } catch (error) {
    clearAuth();
    throw error;
  }
}

// Get current user
export async function getCurrentUser() {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return null;
  }

  try {
    const response = await fetchWithAuth(`${API_ENDPOINT}/auth/me`);
    const data = await response.json();

    if (!response.ok) {
      return null;
    }

    setUser(data.user);
    return data.user;
  } catch (error) {
    console.error('Get user error:', error);
    return null;
  }
}

// Fetch with automatic token refresh
export async function fetchWithAuth(url, options = {}) {
  let accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('Not authenticated');
  }

  // First attempt
  let response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  });

  // If token expired, try to refresh
  if (response.status === 401) {
    const data = await response.json();

    if (data.error === 'TokenExpired') {
      try {
        accessToken = await refreshAccessToken();

        // Retry with new token
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (refreshError) {
        // Refresh failed - redirect to login
        clearAuth();
        window.location.href = '/admin/login';
        throw new Error('Session expired. Please login again.');
      }
    }
  }

  return response;
}

// Helper for authenticated JSON requests
export async function authFetch(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_ENDPOINT}${endpoint}`;

  const response = await fetchWithAuth(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }

  return data;
}

// Auth state change listeners
const authListeners = new Set();

export function onAuthStateChange(callback) {
  authListeners.add(callback);
  return () => authListeners.delete(callback);
}

function notifyAuthStateChange(user) {
  authListeners.forEach(callback => callback(user));
}

// Initialize auth state on page load
export function initAuth() {
  const user = getStoredUser();
  if (user) {
    // Verify token is still valid
    getCurrentUser().then(freshUser => {
      if (!freshUser) {
        clearAuth();
        notifyAuthStateChange(null);
      } else {
        notifyAuthStateChange(freshUser);
      }
    });
  }
  return user;
}
